import React, { useCallback, useEffect, useMemo } from 'react'
import { $TOWNS } from '@components/$TOWNS/$TOWNS'
import { useStakingDeposits } from '@components/ClaimRewardsPanel/hooks/useStakingData'
import { StakingBadge } from '@components/StakingBadge/StakingBadge'
import { Box, FancyButton, Paragraph, Stack } from '@ui'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { Analytics } from 'hooks/useAnalytics'
import { CHANNEL_INFO_PARAMS } from 'routes'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { useTownsTokenBalance } from 'use-towns-client'

export function StakeToTownBanner() {
    const { openPanel } = usePanelActions()

    // Get TOWNS token balance using SpaceDapp
    const { data: abstractAccountAddress } = useMyAbstractAccountAddress()
    const { data: townsBalance } = useTownsTokenBalance({
        address: abstractAccountAddress as `0x${string}`,
    })

    // Check if user has existing staked tokens
    const {
        data: { totalStaked },
        isLoading: isLoadingStakingDeposits,
    } = useStakingDeposits({
        owner: abstractAccountAddress,
        enabled: !!abstractAccountAddress,
    })

    // Check if user has TOWNS tokens using SpaceDapp balance
    const hasTownsTokens = useMemo(() => {
        return (townsBalance ?? 0) > 0
    }, [townsBalance])

    // Check if user has already staked
    const hasAlreadyStaked = useMemo(() => {
        return totalStaked.gt(0)
    }, [totalStaked])

    // Show stake banner when user has towns tokens and hasn't staked yet
    const showBanner = hasTownsTokens && !hasAlreadyStaked

    // Track when user sees the stake prompt
    useEffect(() => {
        if (showBanner) {
            Analytics.getInstance().track('stake prompt', {})
        }
    }, [showBanner])

    const onClickStakeToTown = useCallback(() => {
        openPanel(CHANNEL_INFO_PARAMS.STAKE_TO_TOWN)
    }, [openPanel])

    if (!showBanner || isLoadingStakingDeposits) {
        return null
    }

    return (
        <Box padding cursor="pointer" background="level2" rounded="md" onClick={onClickStakeToTown}>
            <Stack horizontal gap="sm" alignItems="center">
                <StakingBadge size="x3" type="diamond" />
                <Stack grow gap="sm">
                    <Paragraph fontWeight="medium" color="default">
                        Earn yield on <$TOWNS value={townsBalance || 0} />
                    </Paragraph>
                    <Paragraph size="sm">And unlock a verified badge.</Paragraph>
                </Stack>
                <FancyButton cta compact borderRadius="full">
                    Earn yield
                </FancyButton>
            </Stack>
        </Box>
    )
}
